const DATABASE_NAME = 'dixit-device';
const DATABASE_VERSION = 2;
const CHUNKS_STORE_NAME = 'chunks';
const OUTBOX_STORE_NAME = 'outbox';
const LOCAL_INTERVIEWS_STORE_NAME = 'local-interviews';

/**
 * Opens the on-device transfer database, creating its schema on first use.
 *
 * All three stores are declared here, in the one place every adapter shares, so whichever adapter
 * happens to trigger a version upgrade always creates the same schema. Opening is entirely the
 * caller's decision: nothing here runs until this function is actually invoked, and it must never
 * be called from a constructor — only from inside a method that genuinely needs the database, so
 * that visiting a page that never buffers bytes leaves no database on the device.
 *
 * `chunks` holds the opaque fragments, keyed by `['itemId', 'segment', 'index']` with indexes
 * `byItem` (`itemId`) and `byItemSegment` (`['itemId', 'segment']`). `outbox` holds the queue
 * metadata, keyed by `id` with index `byState` (`state`). `local-interviews` holds an interview's
 * metadata for the duration of its capture and transfer, keyed by `id` with index `byOwner`
 * (`ownerUserId`), which serves its port's owner-scoped `snapshot()`.
 *
 * Each `if (!database.objectStoreNames.contains(...))` guard, rather than an unconditional create,
 * is what makes this `onupgradeneeded` handler replayable from any earlier version: it only ever
 * creates what a given device has not created yet, regardless of which version that device is
 * upgrading from.
 */
const openDeviceDatabase = (indexedDbFactory: IDBFactory): Promise<IDBDatabase> => {
	return new Promise((resolve, reject) => {
		const request = indexedDbFactory.open(DATABASE_NAME, DATABASE_VERSION);

		request.onupgradeneeded = () => {
			const database = request.result;

			if (!database.objectStoreNames.contains(CHUNKS_STORE_NAME)) {
				const chunkStore = database.createObjectStore(CHUNKS_STORE_NAME, {
					keyPath: ['itemId', 'segment', 'index'],
				});
				chunkStore.createIndex('byItem', 'itemId');
				chunkStore.createIndex('byItemSegment', ['itemId', 'segment']);
			}

			if (!database.objectStoreNames.contains(OUTBOX_STORE_NAME)) {
				const outboxStore = database.createObjectStore(OUTBOX_STORE_NAME, { keyPath: 'id' });
				outboxStore.createIndex('byState', 'state');
			}

			if (!database.objectStoreNames.contains(LOCAL_INTERVIEWS_STORE_NAME)) {
				const localInterviewsStore = database.createObjectStore(LOCAL_INTERVIEWS_STORE_NAME, {
					keyPath: 'id',
				});
				localInterviewsStore.createIndex('byOwner', 'ownerUserId');
			}
		};

		request.onsuccess = () => {
			resolve(request.result);
		};

		request.onerror = () => {
			reject(request.error ?? new Error("Échec de l'ouverture de la base de stockage locale."));
		};
	});
};

export default openDeviceDatabase;
export { CHUNKS_STORE_NAME, OUTBOX_STORE_NAME, LOCAL_INTERVIEWS_STORE_NAME };
